import { stringArrayParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import type { ImageMetadata } from '../types/metadata.js';
import {
  assessColorProfile,
  classifyColorSpace,
  metadataConfidence,
  srgbRequiredBy,
} from '../technical/index.js';
import { missingRequirementReason, notChecked, plainResult, withRequirement } from './support.js';

/**
 * Colour-profile check: reports the colour space the *file* declares.
 *
 * Two declarations can exist — the EXIF `ColorSpace` tag and an embedded ICC
 * profile — and neither is mandatory. A missing ICC profile is therefore never a
 * failure: when the file states nothing at all the check says `uncertain`
 * instead of inventing a verdict.
 */
export const colorProfileCheck: ValidationCheck = {
  id: 'color_profile',
  title: 'Цветовой профиль',
  description:
    'Проверяет, какое цветовое пространство записано в файле, и соответствует ли оно требованию документа.',
  kind: 'technical',
  requires: ['metadata'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['metadata']);
    if (missing) return notChecked(ctx, colorProfileCheck, missing, ctx.rule.severity);

    const metadata = ctx.fileInfo.metadata as ImageMetadata;
    const exif = metadata.exif;
    const declaration = classifyColorSpace(exif);
    const srgbRequired = srgbRequiredBy(ctx.rule.params, ctx.rule.official);
    const allowedSpaces = stringArrayParam(ctx.rule.params, 'allowedSpaces', []);
    const assessment = assessColorProfile({ declaration, srgbRequired, allowedSpaces });
    const confidence = metadataConfidence(metadata);
    const notes = [
      ...metadata.notes,
      `declared-space:${declaration.kind}`,
      srgbRequired ? 'srgb-required' : 'srgb-not-required',
    ];

    const requirement = srgbRequired
      ? 'Требуется цветовое пространство sRGB.'
      : allowedSpaces.length > 0
        ? `Допустимые цветовые пространства: ${allowedSpaces.join(', ')}.`
        : undefined;

    const message = (() => {
      if (assessment.verdict === 'uncertain') {
        return '⚠ Не удалось определить цветовое пространство файла';
      }
      if (assessment.verdict === 'pass') {
        return '✅ Цветовое пространство соответствует';
      }
      switch (assessment.reason) {
        case 'non-rgb':
          return `Во встроенном профиле указано пространство ${
            declaration.label ?? 'не RGB'
          } — это не RGB.`;
        case 'space-not-allowed':
          return `Цветовое пространство ${declaration.label ?? 'файла'} не входит в список допустимых.`;
        default:
          return 'Цветовое пространство не подтверждено как sRGB, а для документа требуется sRGB.';
      }
    })();


    const result = plainResult({
      id: colorProfileCheck.id,
      title: colorProfileCheck.title,
      status: assessment.verdict,
      severity: ctx.rule.severity,
      message,
      ...(requirement ? { requirement } : {}),
      // A colour profile is not part of the picture: a warning can be fixed by
      // re-exporting from the original, never by editing the photo.
      fixability: assessment.verdict === 'warning' ? 'fixable' : 'none',
      confidence,
      details: {
        iccPresent: exif?.iccProfile?.present ?? false,
        iccDescription: exif?.iccProfile?.description ?? null,
        iccColourSpace: exif?.iccProfile?.colourSpace ?? null,
        exifColorSpace: exif?.colorSpace ?? null,
        srgbRequired,
        allowedSpaces,
        declaredSpace: declaration.kind,
        notes,
      },
    });
    return withRequirement(result, ctx.rule, {});
  },
};
